import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    AcademicCapIcon,
    MagnifyingGlassIcon,
    CalendarDaysIcon,
    TrophyIcon,
    PlusCircleIcon,
    ArrowRightIcon,
    ClockIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    PencilSquareIcon,
    EyeIcon
} from '@heroicons/react/24/outline';

const mockCourses = [
    { id: 1, name: 'Advanced React Hooks', category: 'Technology', duration: '8 hours', status: 'Available', enrolled: 15 },
    { id: 2, name: 'Effective Communication', category: 'Soft Skills', duration: '4 hours', status: 'Enrolled', enrolled: 1 },
    { id: 3, name: 'Project Management Basics', category: 'Management', duration: '6 hours', status: 'Completed', enrolled: 10 },
    { id: 4, name: 'Data Analysis with Python', category: 'Technology', duration: '12 hours', status: 'Available', enrolled: 22 },
    { id: 5, name: 'Leadership & Team Building', category: 'Management', duration: '5 hours', status: 'Available', enrolled: 8 },
];

const mockCertifications = [
    { id: 101, name: 'Certified Scrum Master', employee: 'John Smith', issuedDate: '2024-05-15', expirationDate: '2026-05-15' },
    { id: 102, name: 'Google Cloud Professional', employee: 'Jane Doe', issuedDate: '2023-11-20', expirationDate: '2025-11-20' },
];

const TrainingDevelopment = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [courses, setCourses] = useState(mockCourses);
    const [certifications, setCertifications] = useState(mockCertifications);
    const [openCertifications, setOpenCertifications] = useState(false);

    const filteredCourses = useMemo(() => {
        return courses.filter(course =>
            course.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            course.category.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [courses, searchTerm]);

    const getStatusColor = (status) => {
        switch (status) {
            case 'Available':
                return 'bg-green-100 text-green-800';
            case 'Enrolled':
                return 'bg-blue-100 text-blue-800';
            case 'Completed':
                return 'bg-purple-100 text-purple-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-100 to-blue-100 p-8">
            <motion.div
                initial={{ opacity: 0, y: -30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="max-w-7xl mx-auto"
            >
                {/* Header & Controls */}
                <div className="flex flex-col md:flex-row items-center justify-between mb-8 pb-4 border-b-2 border-purple-400">
                    <h1 className="text-4xl font-extrabold text-purple-800 tracking-tight mb-4 md:mb-0">
                        Training & Development
                    </h1>
                    <div className="flex items-center space-x-4">
                        <div className="relative">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search courses..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-300 shadow-sm"
                            />
                        </div>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="flex items-center space-x-2 px-6 py-2 bg-purple-600 text-white rounded-full shadow-lg hover:bg-purple-700 transition-colors"
                        >
                            <PlusCircleIcon className="h-5 w-5" />
                            <span className="font-semibold">Add New Course</span>
                        </motion.button>
                    </div>
                </div>

                {/* Course Catalog Section */}
                <motion.div
                    className="bg-white rounded-3xl shadow-xl p-6 mb-8"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.6 }}
                >
                    <div className="flex items-center space-x-2 mb-6 text-purple-600">
                        <AcademicCapIcon className="h-7 w-7" />
                        <h2 className="text-2xl font-bold">Course Catalog</h2>
                    </div>

                    <AnimatePresence>
                        {filteredCourses.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {filteredCourses.map(course => (
                                    <motion.div
                                        key={course.id}
                                        className="bg-gray-50 rounded-xl shadow-sm p-5 border border-gray-200 hover:shadow-md transition-shadow duration-300"
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.9 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <div className="flex items-center justify-between mb-3">
                                            <h3 className="text-lg font-bold text-gray-800">{course.name}</h3>
                                            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(course.status)}`}>
                                                {course.status}
                                            </span>
                                        </div>
                                        <div className="flex items-center space-x-4 text-sm text-gray-500 mb-2">
                                            <div className="flex items-center space-x-1">
                                                <CalendarDaysIcon className="h-4 w-4" />
                                                <span>{course.duration}</span>
                                            </div>
                                            <div className="flex items-center space-x-1">
                                                <ClockIcon className="h-4 w-4" />
                                                <span>{course.category}</span>
                                            </div>
                                        </div>
                                        <p className="text-gray-600 text-sm">
                                            Enrolled: <span className="font-semibold text-gray-800">{course.enrolled}</span>
                                        </p>
                                        <div className="mt-4 flex justify-end space-x-2">
                                            <motion.button
                                                whileHover={{ scale: 1.1 }}
                                                whileTap={{ scale: 0.9 }}
                                                className="text-indigo-600 hover:text-indigo-900 transition-colors"
                                            >
                                                <EyeIcon className="h-5 w-5" />
                                            </motion.button>
                                            <motion.button
                                                whileHover={{ scale: 1.1 }}
                                                whileTap={{ scale: 0.9 }}
                                                className="text-purple-600 hover:text-purple-900 transition-colors"
                                            >
                                                <PencilSquareIcon className="h-5 w-5" />
                                            </motion.button>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        ) : (
                            <motion.div
                                key="no-courses"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="p-10 text-center text-gray-500 rounded-xl border-dashed border-2 border-gray-300"
                            >
                                <AcademicCapIcon className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                                <p className="text-lg font-medium">No courses found.</p>
                                <p className="text-sm mt-1">Try a different search query or add a new course.</p>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>

                {/* Certifications Section */}
                <motion.div
                    className="bg-white rounded-3xl shadow-xl p-6"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.6 }}
                >
                    <div className="flex items-center justify-between cursor-pointer" onClick={() => setOpenCertifications(!openCertifications)}>
                        <div className="flex items-center space-x-2 text-purple-600">
                            <TrophyIcon className="h-7 w-7" />
                            <h2 className="text-2xl font-bold">Employee Certifications</h2>
                        </div>
                        <motion.div
                            animate={{ rotate: openCertifications ? 180 : 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            <ChevronDownIcon className="h-6 w-6 text-gray-500" />
                        </motion.div>
                    </div>

                    <AnimatePresence>
                        {openCertifications && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.4 }}
                                className="overflow-hidden mt-6"
                            >
                                {certifications.length > 0 ? (
                                    <div className="space-y-4">
                                        {certifications.map(cert => (
                                            <motion.div
                                                key={cert.id}
                                                className="bg-gray-50 rounded-xl shadow-sm p-4 border border-gray-200"
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.3 }}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <h4 className="font-semibold text-gray-800">{cert.name}</h4>
                                                        <p className="text-sm text-gray-600">Certified Employee: {cert.employee}</p>
                                                    </div>
                                                    <div className="text-sm text-gray-500 text-right">
                                                        <p>Issued: {cert.issuedDate}</p>
                                                        <p>Expires: <span className="font-medium">{cert.expirationDate}</span></p>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="p-6 text-center text-gray-400">
                                        <TrophyIcon className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                                        <p>No certifications to display.</p>
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>
            </motion.div>
        </div>
    );
};

export default TrainingDevelopment;